/** Carriers va Jobs API hook'lari. Kalitlar ["carrier"|"job"|"application", ...]: real-time hodisalar shularni yangilaydi. */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { DriverListItem } from "@/features/drivers/types";
import { api, getAllPages, useIdempotencyKeys, type Paginated, type RequestOptions } from "@/lib/api";
import type {
  Carrier,
  CarrierCandidate,
  CarrierOption,
  CarrierWrite,
  EligibleDriver,
  Job,
  JobCandidates,
  JobWrite,
  RejectionReason,
  SubmitResult,
  Truck,
  TruckWrite,
} from "./types";

type Query = RequestOptions["query"];

export const carrierKeys = {
  all: ["carrier"] as const,
  list: (q: Query) => ["carrier", "list", q] as const,
  detail: (id: number | string) => ["carrier", "detail", String(id)] as const,
  candidates: (id: number | string, page: number) => ["application", "carrier", String(id), page] as const,
  reasons: (id: number | string) => ["application", "carrier-reasons", String(id)] as const,
};

export const jobKeys = {
  all: ["job"] as const,
  list: (q: Query) => ["job", "list", q] as const,
  counts: ["job", "counts"] as const,
  detail: (id: number | string) => ["job", "detail", String(id)] as const,
  candidates: (id: number | string) => ["application", "job", String(id)] as const,
  eligible: (id: number | string, q: string) => ["driver", "eligible", String(id), q] as const,
};

export const truckKeys = {
  all: ["truck"] as const,
  list: (q: Query) => ["truck", "list", q] as const,
};

// ---- Carriers ----

export function useCarriers(query: Query) {
  return useQuery({
    queryKey: carrierKeys.list(query),
    queryFn: () => api.get<Paginated<Carrier>>("/api/carriers/", { query }),
    placeholderData: keepPreviousData,
  });
}

/** Tanlov ro'yxatlari uchun hamma carrier (sahifalashsiz): id, nom, holat */
export function useCarrierOptions() {
  return useQuery({
    queryKey: ["carrier", "options"] as const,
    queryFn: () => api.get<CarrierOption[]>("/api/carriers/options/"),
    staleTime: 60_000,
  });
}

export function useCarrier(id: number | string | undefined) {
  return useQuery({
    queryKey: carrierKeys.detail(id ?? ""),
    queryFn: () => api.get<Carrier>(`/api/carriers/${id}/`),
    enabled: id !== undefined && id !== "" && id !== null,
  });
}

export function useCreateCarrier() {
  const qc = useQueryClient();
  const keys = useIdempotencyKeys();
  return useMutation({
    mutationFn: (data: CarrierWrite) => api.post<Carrier>("/api/carriers/", data, { idempotencyKey: keys.for(data) }),
    onSuccess: (_result, data) => {
      keys.done(data);
      qc.invalidateQueries({ queryKey: carrierKeys.all });
    },
  });
}

export function useUpdateCarrier(id: number | string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<CarrierWrite> & { version: number }) => api.patch<Carrier>(`/api/carriers/${id}/`, data),
    onSuccess: (data) => {
      qc.setQueryData(carrierKeys.detail(id), data);
      qc.invalidateQueries({ queryKey: carrierKeys.all });
      qc.invalidateQueries({ queryKey: jobKeys.all });
    },
    onSettled: (_data, error) => {
      // 409: boshqa birov o'zgartirgan -- joriy holat qayta so'raladi
      if (error) qc.invalidateQueries({ queryKey: carrierKeys.detail(id) });
    },
  });
}

export function useArchiveCarrier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/carriers/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: carrierKeys.all }),
  });
}

export function useCarrierCandidates(id: number | string | undefined, page = 1, pageSize = 25) {
  return useQuery({
    queryKey: [...carrierKeys.candidates(id ?? "", page), pageSize],
    queryFn: () => api.get<Paginated<CarrierCandidate>>(`/api/carriers/${id}/candidates/`, { query: { page, page_size: pageSize } }),
    enabled: !!id,
    placeholderData: keepPreviousData,
  });
}

export function useCarrierRejectionReasons(id: number | string | undefined) {
  return useQuery({
    queryKey: carrierKeys.reasons(id ?? ""),
    queryFn: () => api.get<RejectionReason[]>(`/api/carriers/${id}/rejection-reasons/`),
    enabled: !!id,
  });
}

// ---- Jobs ----

export function useJobs(query: Query) {
  return useQuery({
    queryKey: jobKeys.list(query),
    queryFn: () => api.get<Paginated<Job>>("/api/jobs/", { query }),
    placeholderData: keepPreviousData,
  });
}

/** Sarlavha uchun: nechta open / filled / closed (uchta kichik so'rov) */
export function useJobCounts() {
  return useQuery({
    queryKey: jobKeys.counts,
    queryFn: async () => {
      const [open, filled, closed] = await Promise.all(
        (["open", "filled", "closed"] as const).map((status) => api.get<Paginated<Job>>("/api/jobs/", { query: { status, page_size: 1 } })),
      );
      return { open: open.count, filled: filled.count, closed: closed.count };
    },
    staleTime: 30_000,
  });
}

export function useJob(id: number | string | undefined) {
  return useQuery({
    queryKey: jobKeys.detail(id ?? ""),
    queryFn: () => api.get<Job>(`/api/jobs/${id}/`),
    enabled: !!id,
  });
}

export function useCreateJob() {
  const qc = useQueryClient();
  const keys = useIdempotencyKeys();
  return useMutation({
    mutationFn: (data: JobWrite) => api.post<Job>("/api/jobs/", data, { idempotencyKey: keys.for(data) }),
    onSuccess: (_result, data) => {
      keys.done(data);
      qc.invalidateQueries({ queryKey: jobKeys.all });
      qc.invalidateQueries({ queryKey: carrierKeys.all });
    },
  });
}

export function useUpdateJob(id: number | string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<JobWrite> & { version: number }) => api.patch<Job>(`/api/jobs/${id}/`, data),
    onSuccess: (data) => {
      qc.setQueryData(jobKeys.detail(id), data);
      qc.invalidateQueries({ queryKey: jobKeys.all });
      qc.invalidateQueries({ queryKey: carrierKeys.all });
    },
    onSettled: (_data, error) => {
      if (error) qc.invalidateQueries({ queryKey: jobKeys.detail(id) });
    },
  });
}

export function useJobCandidates(id: number | string | undefined) {
  return useQuery({
    queryKey: jobKeys.candidates(id ?? ""),
    queryFn: () => api.get<JobCandidates>(`/api/jobs/${id}/candidates/`),
    enabled: !!id,
  });
}

export function useEligibleDrivers(id: number | string | undefined, q: string, enabled = true) {
  return useQuery({
    queryKey: jobKeys.eligible(id ?? "", q),
    // 50 tagacha ko'rinadi, `total` -- jami (ko'p bo'lsa qidiruv bilan toraytiriladi)
    queryFn: () => api.get<{ results: EligibleDriver[]; total: number }>(`/api/jobs/${id}/eligible-drivers/`, { query: { q: q || undefined } }),
    enabled: !!id && enabled,
    placeholderData: keepPreviousData,
  });
}

export function useSubmitDriver(id: number | string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { driver: number; version?: number; note?: string }) => api.post<SubmitResult>(`/api/jobs/${id}/submit/`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: jobKeys.all });
      qc.invalidateQueries({ queryKey: ["application"] });
      qc.invalidateQueries({ queryKey: ["driver"] });
      qc.invalidateQueries({ queryKey: carrierKeys.all });
    },
  });
}

/** Ro'yxatdagi istalgan carrier'ni o'zgartirish (o'ng tugma menyusi): versiya bilan; xatoda ro'yxat qayta so'raladi */
export function usePatchCarrier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<CarrierWrite> & { id: number; version: number }) => api.patch<Carrier>(`/api/carriers/${id}/`, data),
    onSuccess: (data) => {
      qc.setQueryData(carrierKeys.detail(data.id), data);
      qc.invalidateQueries({ queryKey: carrierKeys.all });
      qc.invalidateQueries({ queryKey: jobKeys.all });
    },
    onError: () => qc.invalidateQueries({ queryKey: carrierKeys.all }),
  });
}

export function usePatchJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<JobWrite> & { id: number; version: number }) => api.patch<Job>(`/api/jobs/${id}/`, data),
    onSuccess: (data) => {
      qc.setQueryData(jobKeys.detail(data.id), data);
      qc.invalidateQueries({ queryKey: jobKeys.all });
      qc.invalidateQueries({ queryKey: carrierKeys.all });
    },
    onError: () => qc.invalidateQueries({ queryKey: jobKeys.all }),
  });
}

export function useArchiveJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/jobs/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: jobKeys.all });
      qc.invalidateQueries({ queryKey: carrierKeys.all });
    },
  });
}

// ---- Truck'lar: ko'rish -- hamma xodim, yozish -- carriers_edit (backend tekshiradi) ----

/** Carrier profilidagi vakansiyalar: hammasi (tab soni va ochiqlari shundan) */
export function useCarrierJobs(carrierId: number) {
  return useQuery({
    queryKey: jobKeys.list({ carrier: carrierId, all: 1 }),
    queryFn: () => getAllPages<Job>("/api/jobs/", { carrier: carrierId, ordering: "-created_at" }),
  });
}

/** Truck'ka biriktirish uchun: shu carrierga ishga olingan driverlar (foydalanuvchi doirasida), hammasi */
export function useHiredAtCarrier(carrierId: number, enabled = true) {
  return useQuery({
    queryKey: ["driver", "hired-at-carrier", carrierId],
    queryFn: () => getAllPages<DriverListItem>("/api/drivers/", { status: "hired", hired_carrier: carrierId, ordering: "last_name" }),
    enabled,
  });
}

export function useTrucks(query: Query, enabled = true) {
  return useQuery({
    queryKey: truckKeys.list(query),
    queryFn: () => getAllPages<Truck>("/api/trucks/", query),
    enabled,
    placeholderData: keepPreviousData,
  });
}

/** Saqlash: yangi (POST) yoki tahrir (PATCH, version bilan -- 409 ziddiyat). Carrier va vakansiya sonlari ham yangilanadi */
export function useSaveTruck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id?: number; data: TruckWrite }) => (id ? api.patch<Truck>(`/api/trucks/${id}/`, data) : api.post<Truck>("/api/trucks/", data)),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: truckKeys.all });
      qc.invalidateQueries({ queryKey: carrierKeys.all });
      qc.invalidateQueries({ queryKey: jobKeys.all });
    },
  });
}

export function useArchiveTruck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/trucks/${id}/`),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: truckKeys.all });
      qc.invalidateQueries({ queryKey: carrierKeys.all });
      qc.invalidateQueries({ queryKey: jobKeys.all });
    },
  });
}
